import { useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft, LogOut } from "lucide-react";
import ThemeToggle from "../ThemeToggle";
import { applyVisiteHead, VISITE_THEME_DARK, VISITE_THEME_LIGHT } from "../../lib/visite/head";
import { useAppTheme } from "../../context/ThemeContext";
import { registerVisiteServiceWorker } from "../../lib/visite/sw";
import { getVisiteBridge, isNativeVisiteApp, nativeVisitePlatform } from "../../lib/visite/bridge";
import VisiteInstallPrompt from "./VisiteInstallPrompt";
import VisiteUpdateBanner from "./VisiteUpdateBanner";
import { VisiteChromeContext } from "./chrome";

/**
 * Contenitore di /visite e /visite-login: metadati PWA, service worker e wrapper nativo.
 * window.VisiteWeb.onBack() -> true se la pagina ha gestito il tasto Indietro.
 */
const EMPTY_CHROME = { title: "Le mie visite", subtitle: "", onBack: null };

function barePath(pathname) {
  return pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
}

export default function VisiteShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { effectiveTheme } = useAppTheme();
  const [chrome, setChrome] = useState(EMPTY_CHROME);
  const chromeRef = useRef(chrome);
  chromeRef.current = chrome;

  useEffect(() => applyVisiteHead(), []);

  useEffect(() => {
    const color = effectiveTheme === "dark" ? VISITE_THEME_DARK : VISITE_THEME_LIGHT;
    document.head.querySelectorAll('meta[name="theme-color"]').forEach((m) => m.setAttribute("content", color));
    const b = getVisiteBridge();
    if (b && typeof b.setTheme === "function") b.setTheme(effectiveTheme, color);
  }, [effectiveTheme]);

  useEffect(() => {
    if (!isNativeVisiteApp()) registerVisiteServiceWorker();
  }, []);

  useEffect(() => {
    const path = barePath(location.pathname);
    const atRoot = path === "/visite" || path === "/visite-login";
    window.VisiteWeb = {
      platform: nativeVisitePlatform(),
      onBack: () => {
        const back = chromeRef.current?.onBack;
        if (typeof back === "function") {
          back();
          return true;
        }
        if (atRoot) return false;
        navigate(-1);
        return true;
      },
    };
    return () => {
      delete window.VisiteWeb;
    };
  }, [location.pathname, navigate]);

  useEffect(() => {
    const b = getVisiteBridge();
    if (b && typeof b.ready === "function") b.ready();
  }, []);

  useEffect(() => {
    const onFocus = (e) => {
      const el = e.target;
      if (!(el instanceof HTMLElement) || !el.matches("input, textarea, select")) return;
      window.setTimeout(() => {
        if (typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "center", behavior: "smooth" });
      }, 250);
    };
    document.addEventListener("focusin", onFocus);
    return () => document.removeEventListener("focusin", onFocus);
  }, []);

  const logout = () => {
    localStorage.removeItem("super_admin_token");
    localStorage.removeItem("super_admin_user");
    navigate("/visite-login", { replace: true });
  };

  const showBar = barePath(location.pathname) === "/visite";

  return (
    <div className="visite-app admin-shell min-h-[100dvh] bg-admin-bg text-admin-text" data-testid="visite-shell">
      <VisiteUpdateBanner />
      {showBar ? (
        <header className="sticky top-0 z-30 border-b border-admin-border bg-admin-surface/95 pt-[env(safe-area-inset-top)] backdrop-blur-md">
          <div className="mx-auto flex min-h-16 max-w-5xl items-center gap-3 px-4 py-2">
            {typeof chrome.onBack === "function" ? (
              <button
                type="button"
                className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-admin-text hover:bg-admin-highlight"
                aria-label="Torna all'elenco"
                onClick={chrome.onBack}
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
            ) : (
              <img src="/visite/icons/visite-192.png" alt="" className="h-10 w-10 shrink-0 rounded-xl" />
            )}
            <div className="min-w-0 flex-1">
              <p className="truncate text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-admin-accent">Affidya</p>
              <p className="truncate text-lg font-semibold leading-tight tracking-tight">{chrome.title}</p>
              {chrome.subtitle ? <p className="truncate text-sm text-admin-muted">{chrome.subtitle}</p> : null}
            </div>
            <ThemeToggle variant="admin" className="h-12 w-12" />
            <button
              type="button"
              className="inline-flex h-12 items-center gap-2 rounded-2xl px-3 text-base text-admin-muted hover:bg-admin-highlight hover:text-admin-text"
              onClick={logout}
            >
              <LogOut className="h-5 w-5" /> Esci
            </button>
          </div>
        </header>
      ) : null}
      <main className={showBar ? "mx-auto w-full max-w-5xl px-4 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-5 md:px-6" : ""}>
        <VisiteChromeContext.Provider value={setChrome}>
          <Outlet />
        </VisiteChromeContext.Provider>
      </main>
      <VisiteInstallPrompt enabled={location.pathname !== "/visite-login"} />
    </div>
  );
}
